import { IDelegationStatus, ISubsidiaryAppliedUserDelegations, ISubsidiaryFormUserDelegations, ISubsidiaryUser, MiddleUsersListTableComponent, UserStatus, UsersListSortProp } from '@aston/middle-users-admin';
import { combineToList, PageBaseClass, SortedFilteredList, StoreLoader, IAction, combineEither, ActionType, ListPagingComponent, LoaderSpinnerComponent, } from '@aston/foundation';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { Component, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { Observable, concat } from 'rxjs';
import { Store } from '@ngrx/store';

import { SubsidiaryDetailStoreSelectors, SubsidiaryDetailStoreActions } from '../../../root-store/subsidiary-detail-store';
import { ListActionHandler } from '../../../shared-module/classes/list-handler.class';
import { IAccountInformationModel } from '../../../user-module/models';
import { AppStoreSelectors } from '../../../root-store/app-store';

@Component({
	selector: 'subsidiary-detail-users-page',
	templateUrl: './subsidiary-detail-users-page.component.html',
	imports: [TranslateModule, MiddleUsersListTableComponent, ListPagingComponent, AsyncPipe, LoaderSpinnerComponent]
})
export class SubsidiaryDetailUsersPageComponent extends PageBaseClass implements OnInit {

	private route = inject(ActivatedRoute)
	private store = inject(Store)

	subsidiaryId: number;

	list$: Observable<SortedFilteredList<ISubsidiaryUser, UsersListSortProp>> = combineToList(
		this.store.select(SubsidiaryDetailStoreSelectors.selectUsersList),
		this.store.select(SubsidiaryDetailStoreSelectors.selectUsersListPaging)
	);

	loader = new StoreLoader(
		combineEither(
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryRoleIsLoading),
			this.store.select(SubsidiaryDetailStoreSelectors.selectUsersIsLoading),
			this.store.select(SubsidiaryDetailStoreSelectors.selectUserStatusIsLoading),
			this.store.select(SubsidiaryDetailStoreSelectors.selectAvailableRolesForCurrentSubsidiaryIsLoading),
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegationsIsLoading),
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegationsFormIsLoading)
		),
		concat(
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryRoleError),
			this.store.select(SubsidiaryDetailStoreSelectors.selectUserStatusError),
			this.store.select(SubsidiaryDetailStoreSelectors.selectUsersError),
			this.store.select(SubsidiaryDetailStoreSelectors.selectAvailableRolesForCurrentSubsidiaryError),
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegationsError),
			this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegationsFormError)
		),
	);

	currentUser$: Observable<IAccountInformationModel> = this.store.select(AppStoreSelectors.selectUser);

	currentSubsidiary$ = this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiary).pipe(
		takeUntil(this.destroySubscriptions$)
	);

	roles$ = this.store.select(SubsidiaryDetailStoreSelectors.selectAvailableRolesForCurrentSubsidiary).pipe(
		takeUntil(this.destroySubscriptions$)
	);

	subsidiaryUserDelegations$ = this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegations).pipe(
		takeUntil(this.destroySubscriptions$)
	);

	subsidiaryUserDelegationsForm$ = this.store.select(SubsidiaryDetailStoreSelectors.selectSubsidiaryUserDelegationsForm).pipe(
		takeUntil(this.destroySubscriptions$)
	);

	listHandler: ListActionHandler;
	userStatus = UserStatus;

	ngOnInit() {
		super.ngOnInit();
		this.subsidiaryId = +this.route.parent.snapshot.params.id;

		this.listHandler = new ListActionHandler().withPagingDispatcher(this.dispatchPagingAction.bind(this));
		this.loadSubsidiary();

		this.currentSubsidiary$.subscribe(_ => {
			this.store.dispatch(SubsidiaryDetailStoreActions.LoadUsersListRequest());
			this.store.dispatch(SubsidiaryDetailStoreActions.LoadAvailableRolesForCurrentSubsidiaryRequest());
		});

	}

	loadSubsidiary() {
		this.store.dispatch(SubsidiaryDetailStoreActions.LoadSubsidiaryRequest(this.subsidiaryId));
	}

	dispatchPagingAction(props) {
		this.store.dispatch(SubsidiaryDetailStoreActions.UpdateUsersListSettings(props));
	}

	updateStatus(action: IAction) {
		this.useDelegationsStatus(action)
			.pipe(first())
			.subscribe(status =>
				this.store.dispatch(SubsidiaryDetailStoreActions.StartUserStatusWorkflow({ action, status })))
	}

	useDelegationsStatus(action: IAction): Observable<IDelegationStatus> {
		if (action.value.userStatus === this.userStatus.Active) {
			this.store.dispatch(SubsidiaryDetailStoreActions.LoadSubsidiaryUserDelegationsFormRequest(action.value.userId))
			return this.subsidiaryUserDelegationsForm$.pipe(
				filter(Boolean),
				map((delegations: ISubsidiaryFormUserDelegations) => delegations.userRoleDelegationIds.length > 0 || delegations.superDebtorDelegationIds.length > 0),
				map(isDelegating => ({ isDelegating }))
			)
		} else {
			this.store.dispatch(SubsidiaryDetailStoreActions.LoadSubsidiaryUserDelegationsRequest(action.value.userId))
			return this.subsidiaryUserDelegations$.pipe(
				filter(Boolean),
				map((delegations: ISubsidiaryAppliedUserDelegations) => delegations.userRoleDelegations.length > 0 || delegations.superDebtorDelegations.length > 0),
				map(isDelegated => ({ isDelegated }))
			)
		}
	}

	updateRole(action: IAction) {
		this.store.dispatch(SubsidiaryDetailStoreActions.UpdateTransientSubsidiaryRoleRequest(action.value));
	}

	updateRegistrationRequest(action: IAction) {
		this.store.dispatch(SubsidiaryDetailStoreActions.ResentUserInvitationRequest(action.value));
	}

	createUser() {
		this.store.dispatch(SubsidiaryDetailStoreActions.OpenSubsidiaryUserForm());
	}

	openDelegationView(action: IAction) {
		this.store.dispatch(SubsidiaryDetailStoreActions.OpenUserDelegationView(action.value));
	}

	openDelegationForm(action: IAction) {
		this.store.dispatch(SubsidiaryDetailStoreActions.OpenUserDelegationForm({
			currentUser: action.value.currentUser,
			user: action.value.user,
			mode: ActionType.UPDATE,
		}));
	}

	openInactivityForm(action: IAction) {
		this.store.dispatch(SubsidiaryDetailStoreActions.OpenUserInactivityForm({
			user: action.value.user,
			mode: ActionType.UPDATE,
		}));
	}
}
